import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Auth from "../helpers/auth.service";
import Request from "../helpers/request.service";
import { authUrl } from "../constants/apiBaseUrl";
import { SUPER_ADMIN_PERMISSIONS, STORE_MANAGER_PERMISSIONS } from "../constants/defaultValues";

export const Role = {
  SUPER_ADMIN: "Super Admin",
  STORE_MANAGER: "Store Manager",
};

export function AuthUser(name, email, role, storeCode, initials) {
  this.name = name;
  this.email = email;
  this.role = role;
  this.storeCode = storeCode;
  this.initials = initials;
}

const ROLE_PERMISSION_FALLBACK = {
  SUPER_ADMIN: SUPER_ADMIN_PERMISSIONS,
  "Super Admin": SUPER_ADMIN_PERMISSIONS,
  STORE_MANAGER: STORE_MANAGER_PERMISSIONS,
  "Store Manager": STORE_MANAGER_PERMISSIONS,
};

const Ctx = createContext(null);

function readUser() {
  if (typeof window === "undefined") return null;
  const u = Auth.user;
  if (!u || Object.keys(u).length === 0) return null;
  if (u.role && typeof u.role !== "string") {
    const flat = u.role.roleName || u.role.name || Role.SUPER_ADMIN;
    const next = { ...u, role: flat, roleId: u.roleId ?? u.role.id ?? null };
    Auth.user = next;
    return next;
  }
  return u;
}

function buildInitials(name, email) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    const a = parts[0]?.[0] || "";
    const b = parts[1]?.[0] || "";
    return (a + b).toUpperCase();
  }
  return (email || "?").slice(0, 2).toUpperCase();
}

export function AuthProvider({ children }) {
  // Synchronous init from storage (lazy initializer) — NOT null-then-effect.
  // A transient-null first render after reload would bounce the shell guard to
  // /login and the public-route guard back to Dashboard, losing the URL. Init
  // synchronously so reload keeps the user on the current page.
  const [user, setUser] = useState(() => readUser());
  const [activeStoreId, setActiveStoreIdState] = useState(() => Auth.activeStoreId);
  const [assignedStores, setAssignedStoresState] = useState(() => Auth.assignedStores);
  // Bumped whenever the permission set changes (loadPermissions). Permissions
  // live in localStorage (not React state), so consumers that gate on them
  // (sidebar nav, route guards) include this in their memo deps to recompute
  // once the post-login bootstrap resolves — otherwise nav entries stay hidden
  // until a manual reload.
  const [permVersion, setPermVersion] = useState(0);

  // user is initialized synchronously above; only re-hydrate store state here.
  useEffect(() => {
    setActiveStoreIdState(Auth.activeStoreId);
    setAssignedStoresState(Auth.assignedStores);
  }, []);

  const setActiveStoreId = useCallback((storeId) => {
    Auth.activeStoreId = storeId;
    setActiveStoreIdState(Auth.activeStoreId);
  }, []);

  const clearActiveStoreId = useCallback(() => {
    Auth.activeStoreId = null;
    setActiveStoreIdState(null);
  }, []);

  const setAssignedStores = useCallback((stores) => {
    Auth.assignedStores = stores;
    setAssignedStoresState(Auth.assignedStores);
  }, []);

  const loginWithTokens = useCallback(
    ({ accessToken, refreshToken, user: serverUser, assignedStores: stores }) => {
      const rawRole = serverUser?.role;
      const role =
        typeof rawRole === "string"
          ? rawRole
          : rawRole?.roleName || rawRole?.name || Role.SUPER_ADMIN;
      const userObj = {
        id: serverUser?.id,
        name: serverUser?.name || serverUser?.email,
        email: serverUser?.email,
        role,
        roleId: serverUser?.roleId ?? rawRole?.id ?? null,
        storeCode: "ALL",
        initials: buildInitials(serverUser?.name, serverUser?.email),
      };

      Auth.token = accessToken;
      Auth.refreshToken = refreshToken;
      Auth.user = userObj;
      // Emergency offline default only — overwritten by the real catalog
      // grants once loadPermissions() runs (server-driven gating, Phase 5).
      Auth.userPermission = ROLE_PERMISSION_FALLBACK[userObj.role] || [];
      Auth.assignedStores = Array.isArray(stores) ? stores : [];

      setUser(userObj);
      setAssignedStoresState(Auth.assignedStores);
    },
    [],
  );

  // Server-driven permission bootstrap (Phase 5). Loads the in-memory
  // permission set from the real catalog grants (/roles/user-actions, which
  // returns every catalog entry with an `allowed` flag) and replaces the
  // static per-role fallback seeded at login. Must run AFTER the active store
  // is set for non-SUPER_ADMIN users — the endpoint is store-context gated.
  // On failure the emergency fallback already in place is retained.
  const loadPermissions = useCallback(async () => {
    try {
      const res = await Request.get(authUrl.role_list_user);
      const list = res?.data?.data;
      if (Array.isArray(list)) {
        const granted = list.filter((p) => p?.allowed);
        Auth.userPermission = granted;
        setPermVersion((v) => v + 1);
        return granted;
      }
    } catch {
      // Keep the emergency fallback seeded by loginWithTokens.
    }
    return Auth.userPermission;
  }, []);

  const logout = useCallback(async () => {
    const refresh = Auth.refreshToken;
    if (refresh) {
      try {
        await Request.postJson(authUrl.logout, { refreshToken: refresh });
      } catch {
        // Ignore — best-effort revoke, FE still clears state.
      }
    }
    Auth.logout();
    setUser(null);
    setActiveStoreIdState(null);
    setAssignedStoresState([]);
  }, []);

  const value = useMemo(
    () => ({
      user,
      permVersion,
      loginWithTokens,
      loadPermissions,
      logout,
      activeStoreId,
      setActiveStoreId,
      clearActiveStoreId,
      assignedStores,
      setAssignedStores,
    }),
    [
      user,
      permVersion,
      loginWithTokens,
      loadPermissions,
      logout,
      activeStoreId,
      setActiveStoreId,
      clearActiveStoreId,
      assignedStores,
      setAssignedStores,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
}

// Legacy route-path → role allow-list. Kept as fallback for AppLayout sidebar
// pre-permission-port. New code should use checkUserURLMethodAccess.
export const MODULE_ACCESS = {
  [Role.SUPER_ADMIN]: [
    "/",
    "/pos",
    "/inventory",
    "/customers",
    "/stores",
    "/users",
    "/settings",
    "/profile",
  ],
  [Role.STORE_MANAGER]: ["/", "/pos", "/inventory", "/customers", "/profile"],
};

export function canAccess(role, path) {
  if (!role) return false;
  return MODULE_ACCESS[role]?.includes(path) || false;
}
